import './news.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Header from '../../../organisms/header/header.js';
import decodeToken from '../../../../utils/decode-token';
import Wadsworth from '../../../organisms/role-hac/wadsworth/wadsworth';
import { useTranslation } from 'react-i18next';
import Footer from "../../../organisms/footer/footer";
import Grid from "@mui/material/Grid2";
import apiClient from '../../../../utils/apiClient';
import { Alert, Snackbar } from '@mui/material';

const formatDate = (value) => {
  if (!value) return '';
  const iso = String(value).replace(' ', 'T');
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(value);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const getUiByStatus = (status, t) => {
  switch (status) {
    case 'not_started':
      return {
        badgeClass: 'not-started',
        badgeText: t('common-not-started'),
        buttonText: t('common-start-survey'),
        iconClass: 'icon-arrow-down',
        noteKey: 'common-due-on',
        dateField: 'due_on',
      };
    case 'in_progress':
      return {
        badgeClass: 'in-progress',
        badgeText: t('common-in-progress'),
        buttonText: t('common-continue-survey'),
        iconClass: 'icon-arrow-down',
        noteKey: 'common-draft-saved-on',
        dateField: 'last_saved_at',
      };
    case 'completed':
    case 'submitted':
      return {
        badgeClass: 'completed',
        badgeText: t('common-completed'),
        buttonText: t('common-view-responses'),
        iconClass: 'icon-arrow-down',
        noteKey: 'common-completed-on',
        dateField: 'submitted_at',
      };
    default:
      return {
        badgeClass: 'in-progress',
        badgeText: status || t('common-in-progress'),
        buttonText: t('common-continue-survey'),
        iconClass: 'icon-arrow-down',
        noteKey: 'common-draft-saved-on',
        dateField: 'last_saved_at',
      };
  }
};

const HacNews = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const token = sessionStorage.getItem('token') || null;
  const decoded = token ? decodeToken(token) : null;
  const role = decoded?.role || null;
  const hacId = decoded?.id || decoded?.hacId || decoded?.user_id;

  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');

  useEffect(() => {
    if (!token || !hacId) return;

    const fetchSurveys = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get(`${process.env.REACT_APP_API_URL}/surveys?hacId=${hacId}`);
        setSurveys(Array.isArray(res.data) ? res.data : []);
      } catch (error) {
        console.error('Error fetching surveys:', error);
        setSnackbarMessage(t('common-internal-server-error'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
    };

    fetchSurveys();
  }, [token, hacId, t]);

  const goToSurvey = (surveyId) => {
    navigate('/hac/survey', {
      state: { surveyId }
    });
  };

  const handleAction = async (survey) => {
    try {
      setActionLoadingId(survey.survey_id);
      const authToken = token;
      const url = `${process.env.REACT_APP_API_URL}/surveys/${survey.survey_id}/start?hacId=${hacId}`;
      const config = {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Cache-Control': 'no-cache',
        },
      };

      if (survey.status === 'not_started') {
        await apiClient.post(url, {}, config);
      }

      goToSurvey(survey.survey_id);
    } catch (error) {
      console.error('Error starting survey:', error);
      setSnackbarMessage(t('common-internal-server-error'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setActionLoadingId(null);
    }
  };

  const content = useMemo(() => {
    if (loading) {
      return <p>{t('common-loading')}</p>;
    }

    if (!surveys.length) {
      return <p>{t('common-no-results') ?? 'No surveys available.'}</p>;
    }

    return (
      <Grid container spacing={3}>
        {surveys.map((survey) => {
          const ui = getUiByStatus(survey.status, t);

          let dateValue = survey?.[ui.dateField];
          if (!dateValue && survey.status === 'not_started') {
            dateValue = survey.started_at || null;
          }

          const noteDate = formatDate(dateValue);
          const isButtonLoading = actionLoadingId === survey.survey_id;

          return (
            <Grid key={survey.survey_id} size={{ xs: 12, sm: 6, md: 4 }}>
              <div className="survey-box">
                <span className={`status ${ui.badgeClass}`}>{ui.badgeText}</span>

                <h4>{t('survey1-title')}</h4>
                {token && (role === 'hac') ? (
                  <p>{t('survey1-description-hac')}</p>
                ): <p>{t('survey1-description')}</p>}

                <button
                  className="btn primary small fit"
                  onClick={() => handleAction(survey)}
                  disabled={isButtonLoading}
                >
                  <span className={ui.iconClass}></span>
                  {isButtonLoading ? t('common-loading') : ui.buttonText}
                </button>

                {noteDate ? (
                  <p className="note">
                    {t(ui.noteKey)} {noteDate}
                  </p>
                ) : null}
              </div>
            </Grid>
          );
        })}
      </Grid>
    );
  }, [loading, surveys, t, actionLoadingId]);

  return (
    <>
      {token ? (
        <div className="App">
          <Header />
          <main className="legal">
            {role === 'hac' && <Wadsworth />}
            <section className="news">
              <div className="inner">
                <h2>{t('common-surveys')}</h2>
                {content}
              </div>
            </section>
          </main>
          <Footer />

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
          >
            <Alert severity={snackbarSeverity} sx={{ width: '100%' }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </div>
      ) : (
        <Navigate to="/login" />
      )}
    </>
  );
};

export default HacNews;